import { Box } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { AiAssistantDrawer } from './AiAssistantDrawer';
import { AiAssistantResizeHandle } from './AiAssistantResizeHandle';
import { useAiAssistant } from './AiAssistantProvider';
import { AI_ASSISTANT_MOBILE_MEDIA_QUERY } from './constants';

export interface AiAssistantLayoutProps {
  children: ReactNode;
}

/**
 * Layout siblings inside AppShell.Main: routed content + resizable AI drawer.
 * Desktop: width-sharing flex. Mobile: overlay when open.
 */
export function AiAssistantLayout({ children }: AiAssistantLayoutProps): JSX.Element {
  const { open, width, setWidth, resetWidth } = useAiAssistant();
  const isMobile = useMediaQuery(AI_ASSISTANT_MOBILE_MEDIA_QUERY) ?? false;
  const containerRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(1200);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setAvailableWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (isMobile) {
    return (
      <Box ref={containerRef} w="100%" style={{ minWidth: 0, position: 'relative' }}>
        {children}
        {/* Keep mounted while a flow exists so conversation state survives close/reopen. */}
        <Box
          style={{
            position: 'fixed',
            inset: 0,
            top: 60,
            zIndex: 150,
            background: 'var(--mantine-color-body)',
            transform: open ? 'translateX(0)' : 'translateX(100%)',
            opacity: open ? 1 : 0,
            visibility: open ? 'visible' : 'hidden',
            pointerEvents: open ? 'auto' : 'none',
            transition: open
              ? 'transform 180ms ease, opacity 180ms ease'
              : 'transform 180ms ease, opacity 180ms ease, visibility 0s linear 180ms',
          }}
          aria-hidden={!open}
        >
          <AiAssistantDrawer mobileOverlay />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      w="100%"
      style={{
        display: 'flex',
        alignItems: 'stretch',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          width: open ? `calc(100% - ${width}px)` : '100%',
          transition: 'width 180ms ease',
        }}
      >
        {children}
      </Box>
      {/* Invisible spacer reserves layout width so the page content shrinks. */}
      <Box
        style={{
          width: open ? width : 0,
          flexShrink: 0,
          transition: 'width 180ms ease',
        }}
        aria-hidden
      />
      {/* The drawer itself is fixed to the viewport: pinned below the 60px header and
          filling the right edge to the bottom of the screen, independent of page scroll,
          route padding, or content height. Kept mounted so in-progress chat survives
          close/reopen on the same document. */}
      <Box
        style={{
          position: 'fixed',
          top: 60,
          right: 0,
          bottom: 0,
          width,
          zIndex: 100,
          display: 'flex',
          overflow: 'hidden',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          pointerEvents: open ? 'auto' : 'none',
          transition: open
            ? 'transform 180ms ease, opacity 180ms ease'
            : 'transform 180ms ease, opacity 180ms ease, visibility 0s linear 180ms',
        }}
        aria-hidden={!open}
      >
        <AiAssistantResizeHandle
          width={width}
          availableMainWidth={availableWidth}
          onWidthChange={setWidth}
          onReset={resetWidth}
        />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <AiAssistantDrawer />
        </Box>
      </Box>
    </Box>
  );
}
